import type { ReactNode } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { Seccion, SeccionPlegable, Senal } from '../../../components/DetalleExpandible';
import { etiquetaBandera } from '../../../domain/pg/catalogo';
import { claveTexto, STOPWORDS_LUGAR } from '../../../domain/pg/normalizar';
import {
  BANDERAS_YI, BLOQUES_BODEGA, BLOQUES_MERCADO, CAMPOS_ELEMENTO_T3, CAMPOS_PRECIO_TRAD, CAMPOS_PROMO_M6,
  CAMPOS_PROMO_T5, MARCAS_MERCADO, META_TRADICIONAL, PREGUNTAS_BODEGA, PREGUNTAS_CIERRE_MER,
  PREGUNTAS_MERCADO, ladoDeClave, ladoDeMarca, type Pregunta,
} from '../../../domain/yichang/catalogo';
import { brecha } from '../../../domain/yichang/comun';
import { codigoDeClave, etiquetaOpcion, humanizarClave, vacia } from '../../../domain/yichang/respuestas';
import {
  esGranel, ladoDePrecio, ladoDePromo, por100Trad, presenciaBodega, presenciaMercado, productoDe,
} from '../../../domain/yichang/tradicional';
import {
  arr, obj, str, strs, NOMBRE_MARCA,
  type CierreYiMercado, type FichaYiBodega, type FichaYiMercado, type Json, type MarcaCliente, type MarcaYi,
} from '../../../domain/yichang/tipos';
import { Fila, Fotos, Objeto, PastillaPresencia, Respuestas, SIN, Valor, soles } from './Respuesta';

const TZ = 'America/Lima';

/** Qué parte de la ficha se dibuja: toda, la de una marca, o lo que no es de ninguna. */
type Parte = 'todo' | MarcaCliente | 'comun';

// ═══════════════════════════════════════════════════════════════════════════
// Las piezas especiales: el precio, el choque, las tablas por marca
// ═══════════════════════════════════════════════════════════════════════════

const monto = (v: unknown): ReactNode => {
  if (v === 'no_hay') return 'No hay';
  if (typeof v === 'number') return soles(v);
  return SIN;
};

/** Un precio del tradicional (T5.1 a T5.5 · m5.1 a m5.7), con el precio por 100 g calculado. */
function PrecioTrad({ p }: { p: Json }) {
  const falta = str(p.falta);
  if (falta !== null) {
    return <p className="text-sm">Falta este lado: <b>{etiquetaOpcion(falta)}</b></p>;
  }
  const x100 = por100Trad(p);
  const omitir = ['producto', 'producto_otro', 'variedad', 'presentacion', 'gramaje', 'gramaje_otro_g', 'unidades',
    'origen', 'precio_regular', 'precio_oferta', 'precio_tarjeta', 'fotos', 'falta', 'precio_x100g',
    'precio_oferta_x100g', 'precio_es_por_kilo', 'declarado', 'obligatorio', 'm5_1_categoria'];
  const nuevas = Object.fromEntries(Object.entries(p).filter(([k]) => !omitir.includes(k)));
  const unidades = p.unidades === 'no_se' ? 'No sé' : typeof p.unidades === 'number' ? String(p.unidades) : null;
  return (
    <div className="space-y-2">
      <div className="grid gap-x-6 gap-y-1 sm:grid-cols-3">
        <Fila num={null} texto={CAMPOS_PRECIO_TRAD.producto}>
          {productoDe(p) ?? SIN}{str(p.variedad) !== null && ` ${str(p.variedad)}`}
          {p.obligatorio === true && <span className="ml-1 text-xs text-gray-500">· obligatorio</span>}
        </Fila>
        {str(p.presentacion) !== null && <Fila num="m5.3" texto={CAMPOS_PRECIO_TRAD.presentacion}>{str(p.presentacion)}</Fila>}
        <Fila num={null} texto={CAMPOS_PRECIO_TRAD.gramaje}>
          {esGranel(p) ? 'A granel: el precio es por kilo'
            : str(p.gramaje) === 'Otro · lo escribo' ? (typeof p.gramaje_otro_g === 'number' ? `${p.gramaje_otro_g} g` : SIN)
              : (str(p.gramaje) ?? SIN)}
        </Fila>
        {unidades !== null && <Fila num="m5.5" texto={CAMPOS_PRECIO_TRAD.unidades}>{unidades}</Fila>}
        <Fila num={null} texto={CAMPOS_PRECIO_TRAD.origen}>
          {str(p.origen) === null ? SIN : etiquetaOpcion(str(p.origen) ?? '')}
        </Fila>
        <Fila num={null} texto={CAMPOS_PRECIO_TRAD.precio_regular}>
          {monto(p.precio_regular)}{esGranel(p) && typeof p.precio_regular === 'number' && ' por kilo'}
        </Fila>
        <Fila num={null} texto={CAMPOS_PRECIO_TRAD.precio_oferta}>{monto(p.precio_oferta)}</Fila>
        <Fila num={null} texto={CAMPOS_PRECIO_TRAD.precio_tarjeta}>{monto(p.precio_tarjeta)}</Fila>
        <Fila num={null} texto="Por 100 g · lo calcula la pantalla">
          {x100 === null
            ? <span className="text-gray-500">{esGranel(p) ? 'no aplica: es a granel' : 'no se puede calcular'}</span>
            : <b className="tabular-nums">{soles(x100)}</b>}
        </Fila>
      </div>
      {arr(p.fotos).length > 0 && <Fotos lista={arr(p.fotos)} alt="Precio" />}
      {Object.keys(nuevas).length > 0 && <Objeto o={nuevas} />}
    </div>
  );
}

/** Un choque de precio (T5.6): el par, los dos lados y la brecha por 100 g. */
function ChoqueTrad({ ch }: { ch: Json }) {
  const a = obj(ch.lado_a);
  const b = obj(ch.lado_b);
  const par = str(ch.par);
  const a100 = a !== null && str(a.falta) === null ? por100Trad(a) : null;
  const b100 = b !== null && str(b.falta) === null ? por100Trad(b) : null;
  const br = a100 !== null && b100 !== null && b100 > 0 ? brecha(a100, b100) : null;
  if (par === null) return <p className="text-sm text-gray-600">No se eligió el par.</p>;
  return (
    <div className="space-y-2">
      <p className="text-sm"><b>{etiquetaOpcion(par)}</b></p>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded border border-gray-200 p-2">
          <p className="mb-1 text-xs font-semibold text-gray-700">Lado A · del cliente</p>
          {a === null ? SIN : <PrecioTrad p={a} />}
        </div>
        <div className="rounded border border-gray-200 bg-gray-50 p-2">
          <p className="mb-1 text-xs font-semibold text-gray-700">Lado B · el rival</p>
          {b === null ? SIN : <PrecioTrad p={b} />}
        </div>
      </div>
      <p className="text-sm">
        {br === null ? <span className="text-gray-500">La brecha por 100 g no se puede calcular con estos datos.</span>
          : <>Por 100 g, {productoDe(a ?? {}) ?? 'el lado A'} está{' '}
            <b>{br === 0 ? 'al mismo precio' : `${Math.abs(br)} % ${br > 0 ? 'más caro' : 'más barato'}`}</b>
            {' '}que {productoDe(b ?? {}) ?? 'el rival'} (precio regular).</>}
      </p>
    </div>
  );
}

function Tarjetas({ lista, rotulos, render }: {
  lista: readonly Json[];
  rotulos?: Record<string, Pregunta>;
  render?: (x: Json) => ReactNode;
}) {
  if (lista.length === 0) return SIN;
  return (
    <ul className="space-y-2">
      {lista.map((x, i) => (
        <li key={i} className="rounded border border-gray-200 p-2">
          {render !== undefined ? render(x) : (
            <Objeto o={x} rotulos={rotulos}
              omitir={Object.keys(x).filter((k) => /_otra$/.test(k) && str(x[k]) === null)} />
          )}
        </li>
      ))}
    </ul>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Qué es de cada marca
// ═══════════════════════════════════════════════════════════════════════════

const ladoDeMarcaMercado = (m: string): MarcaCliente | null =>
  (MARCAS_MERCADO.snacks.includes(m) ? 'pringles' : MARCAS_MERCADO.cereales.includes(m) ? 'kelloggs' : ladoDeMarca(m));

const jsons = (v: unknown): Json[] => arr(v).map(obj).filter((x): x is Json => x !== null);

/** Las listas que mezclan las dos marcas, y cómo se sabe de quién es cada elemento. */
const LISTAS_MIXTAS: Record<string, (x: Json) => MarcaCliente | null> = {
  t3_elementos: (x) => ladoDeMarca(str(x.t3_2_marca)),
  t5_precios: ladoDePrecio,
  t5_choques_otros: (x) => ladoDePrecio(obj(x.lado_a) ?? {}) ?? ladoDePrecio(obj(x.lado_b) ?? {}),
  t5_promos: ladoDePromo,
  m5_precios: ladoDePrecio,
  m6_promos: ladoDePromo,
};

const tocaA = (lado: MarcaCliente | null, parte: Parte): boolean =>
  parte === 'todo' || (parte === 'comun' ? lado === null : lado === parte);

/**
 * La porción de una respuesta que es de `parte`, o `undefined` si no le toca nada. Las listas
 * mezcladas se recortan elemento por elemento; el mapa de m4.1, marca por marca.
 */
function recortar(k: string, v: unknown, parte: Parte): unknown {
  if (parte === 'todo') return v;
  if (k in LISTAS_MIXTAS) {
    const r = jsons(v).filter((x) => tocaA(LISTAS_MIXTAS[k](x), parte));
    return r.length === 0 ? undefined : r;
  }
  if (k === 'm4_1_estado_por_marca') {
    const o = obj(v) ?? {};
    const r = Object.fromEntries(Object.entries(o).filter(([m]) => tocaA(ladoDeMarcaMercado(m), parte)));
    return Object.keys(r).length === 0 ? undefined : r;
  }
  return tocaA(ladoDeClave(k), parte) ? v : undefined;
}

/** T10.2: de qué foto obligatoria es cada motivo (los `id` de `fotosFaltantes` del formulario). */
const FOTO_FALTANTE: Record<string, string> = {
  t3_8: 'Foto del mostrador o exhibidor de snacks (T3.8)',
  t3_9: 'Foto de donde está el cereal (T3.9)',
};
const fotoFaltante = (id: string): string => {
  const m = /^t5_5_(.+)$/.exec(id);
  return m ? `Foto del precio · ${humanizarClave(m[1])} (T5.5)` : humanizarClave(id);
};

/** Una respuesta del tradicional con la pieza que le corresponde. */
function RespuestaTrad({ k, v }: { k: string; v: unknown }) {
  if (k === 'm4_1_estado_por_marca') {
    const o = obj(v) ?? {};
    const filas = Object.entries(o);
    if (filas.length === 0) return SIN;
    return (
      <div className="overflow-x-auto">
      <table className="text-sm">
        <tbody className="divide-y divide-gray-100">
          {filas.map(([m, e]) => (
            <tr key={m}>
              <td className="py-0.5 pr-4">{m}</td>
              <td className="py-0.5">{typeof e === 'string' ? etiquetaOpcion(e, k) : <Valor v={e} />}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    );
  }
  if (k === 't3_elementos') return <Tarjetas lista={jsons(v)} rotulos={CAMPOS_ELEMENTO_T3} />;
  if (k === 't5_promos') return <Tarjetas lista={jsons(v)} rotulos={CAMPOS_PROMO_T5} />;
  if (k === 'm6_promos') return <Tarjetas lista={jsons(v)} rotulos={CAMPOS_PROMO_M6} />;
  if (k === 't5_precios' || k === 'm5_precios') return <Tarjetas lista={jsons(v)} render={(x) => <PrecioTrad p={x} />} />;
  if (k === 't5_choques_otros') return <Tarjetas lista={jsons(v)} render={(x) => <ChoqueTrad ch={x} />} />;
  const o = obj(v);
  if (o !== null && ('lado_a' in o || 'lado_b' in o)) return <ChoqueTrad ch={o} />;
  if (o !== null && 'precio_regular' in o) return <PrecioTrad p={o} />;
  if (k === 't10_2_motivos' || k === 't10_2_otro') {
    // Las llaves son las de `fotosFaltantes` del formulario (`t3_8`, `t3_9`, `t5_5_<precio>`), no
    // preguntas: sin esto se leían «T3 9: Se me pasó».
    if (o === null) return SIN;
    return (
      <ul className="space-y-0.5 text-sm">
        {Object.entries(o).map(([id, m]) => (
          <li key={id}><span className="text-gray-600">{FOTO_FALTANTE[id] ?? fotoFaltante(id)}:</span> <Valor v={m} /></li>
        ))}
      </ul>
    );
  }
  if (k === 't7_4_a_cuanto_pringles' || k === 't7_4_a_cuanto_kelloggs') {
    if (o === null) return SIN;
    if (str(o.estado) !== null) return etiquetaOpcion(str(o.estado) ?? '');
    return (
      <Objeto o={o} omitir={['estado']} rotulos={{ producto: 'Producto', gramaje: 'Gramaje', precio: 'Precio que dice (S/)' }} />
    );
  }
  return <Valor v={v} clave={k} />;
}

// ═══════════════════════════════════════════════════════════════════════════
// Los bloques
// ═══════════════════════════════════════════════════════════════════════════

interface Bloque { id: string; prefijo: RegExp; titulo: string }

/** Las llaves de un bloque: primero las del catálogo, en su orden; después las nuevas, por código. */
function llavesDe(ficha: Json, b: Bloque, preguntas: Record<string, Pregunta>): string[] {
  const conocidas = Object.keys(preguntas).filter((k) => b.prefijo.test(k) && k in ficha);
  const nuevas = Object.keys(ficha)
    .filter((k) => b.prefijo.test(k) && !(k in preguntas) && !META_TRADICIONAL.includes(k))
    .sort((x, y) => (codigoDeClave(x) ?? x).localeCompare(codigoDeClave(y) ?? y, 'es', { numeric: true }));
  return [...conocidas, ...nuevas];
}

function Bloques({ ficha, bloques, preguntas, parte, saltar = [], sinLlaves = [] }: {
  ficha: Json;
  bloques: readonly Bloque[];
  preguntas: Record<string, Pregunta>;
  parte: Parte;
  saltar?: readonly string[];
  /** Llaves que se dibujan en otra parte del detalle: acá no se repiten. */
  sinLlaves?: readonly string[];
}) {
  const hechos = bloques.filter((b) => !saltar.includes(b.id)).map((b) => {
    const filas = llavesDe(ficha, b, preguntas)
      .filter((k) => !sinLlaves.includes(k))
      .map((k) => ({ k, v: recortar(k, ficha[k], parte) }))
      // En blanco no se dibuja: el formulario vacía lo que esconde (T4c a quien sí la trabaja, T7 de una
      // marca que no trabaja…), y lo obligatorio que faltó ya está en «Lo que quedó sin contestar».
      .filter((x) => x.v !== undefined && !vacia(x.v));
    return { b, filas };
  }).filter((x) => x.filas.length > 0);
  if (hechos.length === 0) return null;
  return (
    <>
      {hechos.map(({ b, filas }) => (
        <Seccion key={b.id} titulo={b.titulo} ancho>
          <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            {filas.map(({ k, v }) => {
              const [num, texto] = preguntas[k] ?? [codigoDeClave(k), humanizarClave(k)];
              const largo = k in LISTAS_MIXTAS || /choque|precio|mas_barat|elementos|promos|estado_por_marca/.test(k);
              return (
                <div key={k} className={largo ? 'sm:col-span-2' : undefined}>
                  <Fila num={num} texto={texto}><RespuestaTrad k={k} v={v} /></Fila>
                </div>
              );
            })}
          </div>
        </Seccion>
      ))}
    </>
  );
}

/** Lo que ninguna sección reclamó: una llave nueva sin prefijo conocido. Nunca se pierde. */
function Sueltas({ ficha, bloques }: { ficha: Json; bloques: readonly Bloque[] }) {
  const sueltas = Object.fromEntries(Object.entries(ficha).filter(([k]) =>
    !META_TRADICIONAL.includes(k) && !bloques.some((b) => b.prefijo.test(k))));
  if (Object.keys(sueltas).length === 0) return null;
  return <Seccion titulo="Otras respuestas" ancho><Respuestas o={sueltas} preguntas={{}} /></Seccion>;
}

function Visita({ lat, lng, origin, registradoEn, duracionSeg, flags, faltantes, extra }: {
  lat: number | null; lng: number | null; origin: string | null; registradoEn: string | null;
  duracionSeg: number | null; flags: readonly string[]; faltantes: readonly string[]; extra?: ReactNode;
}) {
  return (
    <Seccion titulo="La visita" ancho>
      <div className="space-y-3">
        <div className="grid gap-x-6 gap-y-2 sm:grid-cols-3">
          <Fila num={null} texto="Coordenadas">
            {lat === null || lng === null ? <Senal tono="neutro">sin GPS</Senal> : (
              <a className="text-blue-700 underline" target="_blank" rel="noreferrer"
                href={`https://www.google.com/maps?q=${lat},${lng}`}>{lat.toFixed(5)}, {lng.toFixed(5)}</a>
            )}
          </Fila>
          <Fila num={null} texto="Se envió">{origin === 'offline_sync' ? 'sin señal, desde la cola' : 'con señal'}</Fila>
          <Fila num={null} texto="Llegó al servidor">
            {registradoEn === null ? SIN : formatInTimeZone(new Date(registradoEn), TZ, 'dd/MM/yyyy HH:mm')}
            {duracionSeg !== null && <span className="block text-xs text-gray-500">la ficha tomó {Math.round(duracionSeg / 60)} min</span>}
          </Fila>
        </div>
        {flags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {flags.map((x) => { const e = etiquetaBandera(x, BANDERAS_YI); return <Senal key={x} tono={e.tono}>{e.label}</Senal>; })}
          </div>
        )}
        {extra}
        <p className="text-xs text-gray-500">
          Las preguntas en blanco no se muestran: el formulario vacía las que no correspondían, y las obligatorias que
          faltaron están en la lista de abajo.
        </p>
        {faltantes.length > 0 && (
          <SeccionPlegable
            titulo="Lo que quedó sin contestar al enviar"
            extra={<span className="text-xs text-gray-500">{faltantes.length}</span>}
          >
            <p className="mb-1 text-xs text-gray-600">
              La lista que armó el propio formulario en el momento de enviar. El supervisor eligió enviar igual.
            </p>
            <ul className="list-inside list-disc text-sm">{faltantes.map((x) => <li key={x}>{x}</li>)}</ul>
          </SeccionPlegable>
        )}
      </div>
    </Seccion>
  );
}

/**
 * Con una marca: su parte, y al final lo que es de la visita, plegado. Con las dos: todo.
 *
 * Las fotos de la visita (la fachada, el puesto) no son de una marca, pero son la evidencia de las
 * dos: con una marca enfocada van a la vista, arriba, y no dentro de lo plegado.
 */
function PorMarca({ ficha, bloques, preguntas, marca, fijos, saltar = [], fotosVisita = [] }: {
  ficha: Json;
  bloques: readonly Bloque[];
  preguntas: Record<string, Pregunta>;
  marca: MarcaYi;
  /** Los bloques que identifican la visita: van siempre enteros y arriba. */
  fijos: readonly string[];
  saltar?: readonly string[];
  fotosVisita?: readonly string[];
}) {
  const identidad = bloques.filter((b) => fijos.includes(b.id));
  const resto = bloques.filter((b) => !fijos.includes(b.id));
  const fotos = fotosVisita.filter((k) => !vacia(ficha[k]));
  return (
    <>
      <Bloques ficha={ficha} bloques={identidad} preguntas={preguntas} parte="todo" />
      {marca === 'las_dos' ? (
        <Bloques ficha={ficha} bloques={resto} preguntas={preguntas} parte="todo" saltar={saltar} />
      ) : (
        <>
          {fotos.length > 0 && (
            <Seccion titulo="Fotos de la visita" ancho>
              <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                {fotos.map((k) => {
                  const [num, texto] = preguntas[k] ?? [codigoDeClave(k), humanizarClave(k)];
                  return <Fila key={k} num={num} texto={texto}><Valor v={ficha[k]} clave={k} alt={texto} /></Fila>;
                })}
              </div>
            </Seccion>
          )}
          <Bloques ficha={ficha} bloques={resto} preguntas={preguntas} parte={marca} saltar={saltar} />
          <SeccionPlegable titulo="Lo demás de la visita · no es de una marca" ancho>
            <div className="space-y-4">
              <Bloques ficha={ficha} bloques={resto} preguntas={preguntas} parte="comun" saltar={saltar} sinLlaves={fotos} />
            </div>
          </SeccionPlegable>
        </>
      )}
      <Sueltas ficha={ficha} bloques={bloques} />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Bodega y puesto
// ═══════════════════════════════════════════════════════════════════════════

export function DetalleYiBodega({ ficha: f, marca }: { ficha: FichaYiBodega; marca: MarcaYi }) {
  const fotosFaltan = strs(f.ficha.fotos_obligatorias_faltantes);
  return (
    <>
      <Visita lat={f.lat} lng={f.lng} origin={f.origin} registradoEn={f.registradoEn} duracionSeg={f.duracionSeg}
        flags={f.flags} faltantes={strs(f.ficha.faltantes)}
        extra={(
          <>
            <p className="text-sm">
              ¿Aceptó dar su contacto?{' '}
              <b>{f.contactoConsentido === true ? 'Sí' : f.contactoConsentido === false ? 'No' : 'Sin dato'}</b>
              <span className="block text-xs text-gray-500">
                El contacto no se muestra acá: es de un tercero y se guarda aparte, con acceso restringido.
              </span>
            </p>
            {fotosFaltan.length > 0 && (
              <p className="text-sm text-amber-900">
                {fotosFaltan.length} {fotosFaltan.length === 1 ? 'foto obligatoria faltó' : 'fotos obligatorias faltaron'};
                el motivo va en T10.2.
              </p>
            )}
            {marca !== 'las_dos' && (
              <p className="text-sm">
                {NOMBRE_MARCA[marca]} en esta bodega: <PastillaPresencia p={presenciaBodega(f, marca)} />
              </p>
            )}
          </>
        )}
      />
      <PorMarca ficha={f.ficha} bloques={BLOQUES_BODEGA} preguntas={PREGUNTAS_BODEGA} marca={marca} fijos={['e']}
        fotosVisita={['t2_1_fotos_fachada']} />
    </>
  );
}

const claveMercado = (n: string | null): string => claveTexto(n, STOPWORDS_LUGAR) ?? '—';

export function DetalleYiMercado({ ficha: f, marca, cierres }: {
  ficha: FichaYiMercado; marca: MarcaYi; cierres: readonly CierreYiMercado[];
}) {
  const nada = strs(f.ficha.m2_2_categorias).includes('nada');
  // Un puesto que no se pudo levantar solo contestó m1, y uno que no vende estas categorías saltó de
  // m2 a la foto. Dibujar los bloques vacíos se leería como un registro incompleto de un puesto que
  // en realidad está completo.
  const saltar = !f.levantado ? BLOQUES_MERCADO.map((b) => b.id).filter((id) => id !== 'me' && id !== 'm1')
    : nada ? ['m3', 'm4', 'm5', 'm6', 'm7', 'm8'] : [];
  const delDia = cierres.filter((c) => c.fecha === f.fecha && claveMercado(c.mercadoNombre) === claveMercado(f.mercadoNombre))
    // El último por la hora del celular y, si falta, por la de llegada: el formulario dice que vale el último.
    .sort((a, b) => (b.ocurridoEn ?? b.registradoEn ?? '').localeCompare(a.ocurridoEn ?? a.registradoEn ?? ''));
  return (
    <>
      <Visita lat={f.lat} lng={f.lng} origin={f.origin} registradoEn={f.registradoEn} duracionSeg={f.duracionSeg}
        flags={f.flags} faltantes={strs(f.ficha.faltantes)}
        extra={marca !== 'las_dos' && (
          <p className="text-sm">
            {NOMBRE_MARCA[marca]} en este puesto: <PastillaPresencia p={presenciaMercado(f, marca)} />
          </p>
        )}
      />
      <PorMarca ficha={f.ficha} bloques={BLOQUES_MERCADO} preguntas={PREGUNTAS_MERCADO} marca={marca}
        fijos={['me', 'm1']} saltar={saltar} fotosVisita={['m9_1_fotos_puesto']} />
      {!f.levantado && (
        <p className="text-sm text-gray-600">
          El puesto no se pudo levantar{f.motivoNo !== null ? ` (${f.motivoNo})` : ''}: del bloque m2 en adelante el
          formulario no preguntó nada. No son respuestas que faltan.
        </p>
      )}
      {f.levantado && nada && (
        <p className="text-sm text-gray-600">
          El puesto no vende snacks, cereales ni avena: de m3 a m8 el formulario no preguntó nada.
        </p>
      )}
      <Seccion titulo="Cierre de la jornada de este mercado" ancho>
        {delDia.length === 0 ? (
          <p className="text-sm text-gray-600">Este mercado no tiene cierre de jornada para el {f.fecha}.</p>
        ) : (
          <div className="space-y-2">
            {delDia.length > 1 && (
              <p className="text-xs text-gray-600">
                Se cerró {delDia.length} veces ese día: el formulario dice que vale el último, que es el de arriba.
              </p>
            )}
            {/* Sin el supervisor ni el mercado: son los de la ficha de arriba (el cierre se cruzó por ellos). */}
            <Respuestas o={delDia[0].ficha} preguntas={PREGUNTAS_CIERRE_MER}
              omitir={['version', 'instrumento', 'me1_supervisor', 'me2_mercado', 'mercado_escrito_a_mano']} />
          </div>
        )}
      </Seccion>
    </>
  );
}
